// /src/Components/Services/DomainServices/useDomain.js

import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { GlobalContext } from "../../../Context/GlobalContext";

// ======================================================
// Fixed domain configuration
// ======================================================

const FIXED_DOMAIN = "chaafo.com";
const FIXED_CONTENT_IP = "34.131.63.76";

const INITIAL_DEPLOYMENT_STEPS = {
  dns: "idle",
  cpanel: "idle",
  theme: "idle",
  manifest: "idle",
};

const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

const isSuccessResponse = (res) => {
  return res?.data?.responseCode === "00";
};

// ======================================================
// Resolve current admin/user ID
// ======================================================

const getStoredUserId = () => {
  try {
    const rawUser =
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      sessionStorage.getItem("admin") ||
      localStorage.getItem("admin");

    if (!rawUser) return "";

    const user = JSON.parse(rawUser);

    return (
      user?.userid ||
      user?.userId ||
      user?.uscode ||
      user?.usCode ||
      user?.id ||
      user?.pk_user_id ||
      ""
    );
  } catch (error) {
    console.error("User parse error:", error);
    return "";
  }
};

export default function useDomain() {
  const { api, adminId } = useContext(GlobalContext);

  const domain = FIXED_DOMAIN;
  const fixedContentIp = FIXED_CONTENT_IP;

  // ======================================================
  // Template states
  // ======================================================

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateCode, setSelectedTemplateCode] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  // ======================================================
  // Create-domain form states
  // ======================================================

  const [selectedClientId, setSelectedClientId] = useState("");
  const [subdomainName, setSubdomainName] = useState("");

  // Fixed and non-editable content IP
  const [subdomainContent] = useState(FIXED_CONTENT_IP);

  // ======================================================
  // Deployment states
  // ======================================================

  const [creating, setCreating] = useState(false);
  const [createdDomainData, setCreatedDomainData] = useState(null);
  const [updateFileResponse, setUpdateFileResponse] = useState(null);

  const [deploymentSteps, setDeploymentSteps] = useState(
    INITIAL_DEPLOYMENT_STEPS,
  );

  // ======================================================
  // Domains list states
  // ======================================================

  const [subdomains, setSubdomains] = useState([]);
  const [resultInfo, setResultInfo] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyById, setBusyById] = useState({});

  // Existing Domains.jsx compatibility states
  const [newName, setNewName] = useState("");
  const [newContent, setNewContent] = useState("");

  // ======================================================
  // Website states
  // ======================================================

  const [websites, setWebsites] = useState([]);
  const [websitesLoading, setWebsitesLoading] = useState(false);
  const [websiteSyncBusy, setWebsiteSyncBusy] = useState({});

  // ======================================================
  // Common helpers
  // ======================================================

  const getUserId = useCallback(() => {
    return getStoredUserId() || adminId || "";
  }, [adminId]);

  const updateDeploymentStep = useCallback((key, status) => {
    setDeploymentSteps((prev) => ({
      ...prev,
      [key]: status,
    }));
  }, []);

  const resetDeploymentSteps = useCallback(() => {
    setDeploymentSteps(INITIAL_DEPLOYMENT_STEPS);
  }, []);

  const setBusy = (id, value) => {
    setBusyById((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const clearBusy = (id) => {
    setBusyById((prev) => ({
      ...prev,
      [id]: false,
    }));
  };

  // ======================================================
  // Fetch all website templates
  // Automatically select first template
  // ======================================================

  const fetchTemplates = useCallback(async () => {
    const userid = getUserId();

    if (!userid) {
      toast.error("User ID missing for template fetch");
      return [];
    }

    setTemplatesLoading(true);

    try {
      const res = await api.post("/Admin/v1/getAllTemplates", {
        userid,
      });

      if (res?.data?.responseCode !== "00") {
        setTemplates([]);
        setSelectedTemplateId("");
        setSelectedTemplateCode("");

        toast.error(
          res?.data?.responseMessage || "Failed to fetch templates",
        );

        return [];
      }

      const list = Array.isArray(res?.data?.data) ? res.data.data : [];

      setTemplates(list);

      // Keep current selection when it still exists
      const currentTemplate = list.find(
        (item) => String(item?.id) === String(selectedTemplateId),
      );

      if (currentTemplate) {
        setSelectedTemplateId(currentTemplate.id || "");
        setSelectedTemplateCode(currentTemplate.template_code || "");
      } else if (list.length > 0) {
        // Select first template by default
        setSelectedTemplateId(list[0]?.id || "");
        setSelectedTemplateCode(list[0]?.template_code || "");
      } else {
        setSelectedTemplateId("");
        setSelectedTemplateCode("");
      }

      return list;
    } catch (error) {
      console.error("getAllTemplates error:", error);

      setTemplates([]);
      setSelectedTemplateId("");
      setSelectedTemplateCode("");

      toast.error("Something went wrong while fetching templates");

      return [];
    } finally {
      setTemplatesLoading(false);
    }
  }, [api, getUserId, selectedTemplateId]);

  // ======================================================
  // Fetch all DNS subdomains
  // ======================================================

  const fetchSubDomains = useCallback(async () => {
    setLoading(true);

    try {
      const res = await api.post("/Admin/Domain/v1.0/getSubDomain", {
        domain,
      });

      if (res?.data?.responseCode !== "00") {
        setSubdomains([]);
        setResultInfo(null);
        setSummary(null);

        toast.error(
          res?.data?.responseMessage || "Failed to fetch domains",
        );

        return [];
      }

      const data = res?.data?.data;

      const records =
        data?.result?.result ||
        data?.result ||
        data?.records ||
        data?.data ||
        res?.data?.response ||
        data ||
        [];

      const finalRecords = Array.isArray(records) ? records : [];

      setSubdomains(finalRecords);
      setResultInfo(data || null);

      setSummary({
        total: finalRecords.length,
        domain,
      });

      return finalRecords;
    } catch (error) {
      console.error("getSubDomain error:", error);

      setSubdomains([]);
      setResultInfo(null);
      setSummary(null);

      toast.error("Something went wrong while fetching domains");

      return [];
    } finally {
      setLoading(false);
    }
  }, [api, domain]);

  // ======================================================
  // Delete DNS subdomain
  // ======================================================

  const deleteSubDomain = useCallback(
    async (row) => {
      const id = row?.id;

      if (!id) {
        toast.error("Domain ID missing");
        return { success: false };
      }

      setBusy(id, true);

      try {
        const res = await api.post(
          "/Admin/Domain/v1.0/deleteSubDomain",
          {
            domain,
            id,
          },
        );

        if (res?.data?.responseCode !== "00") {
          toast.error(
            res?.data?.responseMessage || "Failed to delete domain",
          );

          return {
            success: false,
            data: res?.data,
          };
        }

        toast.success(
          res?.data?.responseMessage || "Domain deleted successfully",
        );

        setSubdomains((prev) =>
          prev.filter((item) => String(item?.id) !== String(id)),
        );

        return {
          success: true,
          data: res?.data,
        };
      } catch (error) {
        console.error("deleteSubDomain error:", error);
        toast.error("Something went wrong while deleting domain");

        return { success: false };
      } finally {
        clearBusy(id);
      }
    },
    [api, domain],
  );

  // ======================================================
  // Fetch all deployed websites
  // ======================================================

  const fetchWebsites = useCallback(async () => {
    const userid = getUserId();

    if (!userid) {
      toast.error("User ID missing for website fetch");
      return [];
    }

    setWebsitesLoading(true);

    try {
      const res = await api.post("/Admin/v1/getAllWebsites", {
        userid,
      });

      if (res?.data?.responseCode !== "00") {
        setWebsites([]);

        toast.error(
          res?.data?.responseMessage || "Failed to fetch websites",
        );

        return [];
      }

      const list = Array.isArray(res?.data?.data) ? res.data.data : [];

      setWebsites(list);

      return list;
    } catch (error) {
      console.error("getAllWebsites error:", error);

      setWebsites([]);

      toast.error(
        error?.response?.data?.responseMessage ||
          "Something went wrong while fetching websites",
      );

      return [];
    } finally {
      setWebsitesLoading(false);
    }
  }, [api, getUserId]);

  // ======================================================
  // Dynamic PHP manifest updater
  // ======================================================

  const callDynamicUpdateFileApi = async ({ dynamicHost, userid }) => {
    const cleanHost = String(dynamicHost || "")
      .replace(/^https?:\/\//i, "")
      .replace(/\/+$/, "")
      .trim()
      .toLowerCase();

    if (!cleanHost || !userid) {
      return {
        success: false,
        message: "Dynamic host or user ID missing",
        url: "",
        data: null,
      };
    }

    const updateFileUrl = `https://${cleanHost}/update_abhi.php`;

    try {
      const res = await fetch(updateFileUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: userid,
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        return {
          success: false,
          message: "Manifest sync failed",
          url: updateFileUrl,
          data,
        };
      }

      return {
        success: true,
        message: "Manifest synced successfully",
        url: updateFileUrl,
        data,
      };
    } catch (error) {
      console.error("update_abhi.php error:", error);

      return {
        success: false,
        message: "Unable to sync manifest",
        url: updateFileUrl,
        data: null,
      };
    }
  };

  // ======================================================
  // Automatic PHP sync with delay and one retry
  // ======================================================

  const callDynamicUpdateFileApiWithDelayAndRetry = async ({
    dynamicHost,
    userid,
  }) => {
    console.log("Waiting 20 seconds before manifest sync...");

    await sleep(20000);

    const firstAttempt = await callDynamicUpdateFileApi({
      dynamicHost,
      userid,
    });

    if (firstAttempt.success) {
      return {
        ...firstAttempt,
        attempt: 1,
        retried: false,
      };
    }

    console.warn(
      "Manifest sync failed on first attempt. Retrying in 10 seconds...",
    );

    await sleep(10000);

    const secondAttempt = await callDynamicUpdateFileApi({
      dynamicHost,
      userid,
    });

    return {
      ...secondAttempt,
      attempt: 2,
      retried: true,
      firstAttemptResponse: firstAttempt,
    };
  };

  // ======================================================
  // Manual website manifest update
  // ======================================================

  const manuallyUpdateWebsiteManifest = async (website) => {
    const websiteId = website?.id;

    const dynamicHost = String(website?.domain || "")
      .replace(/^https?:\/\//i, "")
      .replace(/\/+$/, "")
      .trim()
      .toLowerCase();

    const userid = String(website?.userid || "").trim();

    if (!websiteId) {
      toast.error("Website ID is missing");
      return { success: false };
    }

    if (!dynamicHost) {
      toast.error("Website domain is missing");
      return { success: false };
    }

    if (!userid) {
      toast.error("Website user ID is missing");
      return { success: false };
    }

    setWebsiteSyncBusy((prev) => ({
      ...prev,
      [websiteId]: true,
    }));

    try {
      const result = await callDynamicUpdateFileApi({
        dynamicHost,
        userid,
      });

      if (!result?.success) {
        toast.error(
          result?.message ||
            "Manual JSON update failed. Please try again later.",
        );

        return result;
      }

      toast.success(`Manifest updated for ${dynamicHost}`);

      setWebsites((prev) =>
        prev.map((item) =>
          String(item?.id) === String(websiteId)
            ? {
                ...item,
                manifest_status: "success",
                manifest_updated_at: new Date().toISOString(),
              }
            : item,
        ),
      );

      return result;
    } catch (error) {
      console.error("Manual manifest update error:", error);

      toast.error(
        "Manual JSON update failed. Please try again later.",
      );

      return {
        success: false,
      };
    } finally {
      setWebsiteSyncBusy((prev) => ({
        ...prev,
        [websiteId]: false,
      }));
    }
  };

  // ======================================================
  // Reset Create Domain form
  // ======================================================

  const resetCreateForm = () => {
    setSelectedClientId("");
    setSubdomainName("");
    setCreatedDomainData(null);
    setUpdateFileResponse(null);

    resetDeploymentSteps();

    if (templates.length > 0) {
      setSelectedTemplateId(templates[0]?.id || "");
      setSelectedTemplateCode(
        templates[0]?.template_code || "",
      );
    } else {
      setSelectedTemplateId("");
      setSelectedTemplateCode("");
    }
  };

  // ======================================================
  // Validate Create Domain form
  // ======================================================

  const validateCreatePayload = () => {
    const cleanName = subdomainName.trim();

    if (!selectedClientId) {
      toast.error("Please select a client");
      return false;
    }

    if (!selectedTemplateId || !selectedTemplateCode) {
      toast.error("Please select a template");
      return false;
    }

    if (!cleanName) {
      toast.error("Restaurant subdomain is required");
      return false;
    }

    if (cleanName.includes(".")) {
      toast.error("Enter only subdomain name, not full domain");
      return false;
    }

    if (!/^[a-zA-Z0-9-]+$/.test(cleanName)) {
      toast.error(
        "Subdomain can only contain letters, numbers and hyphen",
      );
      return false;
    }

    return true;
  };

  // ======================================================
  // Complete create-domain deployment flow
  // ======================================================

  const createDomainFlow = async () => {
    if (!validateCreatePayload()) return;

    const cleanSubdomain = subdomainName.trim().toLowerCase();
    const dynamicHost = `${cleanSubdomain}.${domain}`;

    setCreating(true);
    setCreatedDomainData(null);
    setUpdateFileResponse(null);

    resetDeploymentSteps();

    try {
      // --------------------------------------------------
      // Step 1: Create DNS
      // --------------------------------------------------

      updateDeploymentStep("dns", "running");

      const createDomainRes = await api.post(
        "/Admin/Domain/v1.0/createSubDomain",
        {
          domain,
          subdomainname: cleanSubdomain,
          subdomaincontent: FIXED_CONTENT_IP,
        },
      );

      if (!isSuccessResponse(createDomainRes)) {
        updateDeploymentStep("dns", "error");

        toast.error(
          createDomainRes?.data?.responseMessage ||
            "DNS creation failed",
        );

        return;
      }

      updateDeploymentStep("dns", "done");

      const createdFullDomain =
        createDomainRes?.data?.data?.result?.name ||
        dynamicHost;

      // --------------------------------------------------
      // Step 2: Create cPanel subdomain
      // --------------------------------------------------

      updateDeploymentStep("cpanel", "running");

      const createCpanelRes = await api.post(
        "/Website/v1/CreateCpanelSubDomain",
        {
          domain,
          subdomain: cleanSubdomain,
        },
      );

      if (!isSuccessResponse(createCpanelRes)) {
        updateDeploymentStep("cpanel", "error");

        toast.error(
          createCpanelRes?.data?.responseMessage ||
            "cPanel creation failed",
        );

        return;
      }

      updateDeploymentStep("cpanel", "done");

      // --------------------------------------------------
      // Step 3: Copy selected template
      // --------------------------------------------------

      updateDeploymentStep("theme", "running");

      const copyFilesRes = await api.post(
        "/Admin/v1/CopyCpanelSubDomainFiles",
        {
          domain: createdFullDomain,
          template: selectedTemplateCode,
          templateid: selectedTemplateId,
          userid: selectedClientId,
        },
      );

      if (!isSuccessResponse(copyFilesRes)) {
        updateDeploymentStep("theme", "error");

        toast.error(
          copyFilesRes?.data?.responseMessage ||
            "Theme implementation failed",
        );

        return;
      }

      updateDeploymentStep("theme", "done");

      // --------------------------------------------------
      // Step 4: Update JSON manifest
      // --------------------------------------------------

      updateDeploymentStep("manifest", "running");

      const updateFileRes =
        await callDynamicUpdateFileApiWithDelayAndRetry({
          dynamicHost,
          userid: selectedClientId,
        });

      setUpdateFileResponse(updateFileRes);

      if (!updateFileRes.success) {
        updateDeploymentStep("manifest", "error");

        toast.error(
          "Manifest update failed after 2 attempts. Please try Manual JSON Update from the Websites section after some time.",
          {
            duration: 7000,
          },
        );

        return;
      }

      updateDeploymentStep("manifest", "done");

      // --------------------------------------------------
      // Store final deployment details
      // --------------------------------------------------

      const finalData = {
        fullDomain: createdFullDomain,
        finalUrl: `https://${dynamicHost}`,
        dynamicHost,
        subdomain: cleanSubdomain,
        ip: FIXED_CONTENT_IP,
        template: selectedTemplateCode,
        templateid: selectedTemplateId,
        selectedClientId,
        updateFileUrl: updateFileRes.url,
        domainResponse: createDomainRes?.data,
        cpanelResponse: createCpanelRes?.data,
        copyResponse: copyFilesRes?.data,
        updateFileResponse: updateFileRes?.data,
      };

      setCreatedDomainData(finalData);

      toast.success("Domain deployed successfully");

      setSelectedClientId("");
      setSubdomainName("");

      await Promise.all([
        fetchSubDomains(),
        fetchWebsites(),
      ]);
    } catch (error) {
      console.error("createDomainFlow error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          "Something went wrong while creating domain",
      );
    } finally {
      setCreating(false);
    }
  };

  // ======================================================
  // Initial API fetches
  // ======================================================

  useEffect(() => {
    fetchTemplates();
    fetchSubDomains();
    fetchWebsites();
  }, [fetchTemplates, fetchSubDomains, fetchWebsites]);

  return {
    // Fixed configuration
    domain,
    fixedContentIp,
    subdomainContent,

    // Templates
    templates,
    templatesLoading,
    fetchTemplates,
    selectedTemplateCode,
    setSelectedTemplateCode,
    selectedTemplateId,
    setSelectedTemplateId,

    // Create-domain form
    selectedClientId,
    setSelectedClientId,
    subdomainName,
    setSubdomainName,

    // Deployment
    creating,
    createdDomainData,
    updateFileResponse,
    deploymentSteps,
    updateDeploymentStep,
    resetDeploymentSteps,
    createDomainFlow,
    resetCreateForm,

    // Dynamic updater
    callDynamicUpdateFileApi,
    callDynamicUpdateFileApiWithDelayAndRetry,

    // Domain listing
    subdomains,
    resultInfo,
    summary,
    loading,
    busyById,
    newName,
    newContent,
    setNewName,
    setNewContent,
    fetchSubDomains,
    deleteSubDomain,

    // Existing Domains.jsx aliases
    onFetch: fetchSubDomains,
    onCreate: createDomainFlow,
    onDelete: deleteSubDomain,

    // Website listing
    websites,
    websitesLoading,
    websiteSyncBusy,
    fetchWebsites,
    manuallyUpdateWebsiteManifest,
  };
}